/**
 * Freemium Store - SSR-safe version with Zustand persistence
 *
 * ⭐⭐⭐⭐ [DOC:frontend/src/store/freemium-store.ts] Migrado do frontend antigo
 * ⭐⭐⭐⭐ [WEB:2025] Pattern SSR baseado em Zustand docs oficiais
 *
 * SSR-safe features:
 * - skipHydration: true - evita conflitos entre server/client
 * - storage check: typeof window !== 'undefined'
 * - client-side rehydration manual
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

// =====================================
// Constants (synced with backend)
// =====================================

// Fallback values when API is unavailable
const FALLBACK_QUESTIONS_LIMIT = 30
const FALLBACK_SOFT_PAYWALL_THRESHOLD = 10

// Free category for freemium model (synced with backend FREE_CATEGORY_SLUG)
export const FREE_CATEGORY_SLUG = 'sinalizacao'

// First trophy minimum requirements (display only, enforced by backend)
export const FIRST_TROPHY_MIN_QUESTIONS = 20
export const FIRST_TROPHY_MIN_ACCURACY = 70

// Feature limits for anonymous/free users
export const FREE_FLASHCARDS_LIMIT = 5
export const FREE_SIGNS_LIMIT = 5
export const FREE_SIMULADOS_LIMIT = 1

// Display prices
export const MONTHLY_PRICE = 29.90

// =====================================
// Types
// =====================================

export type FreemiumFeature = 'flashcards' | 'signs' | 'simulados'

interface FreemiumStats {
  totalCorrect: number
  totalAnswered: number
  xpAccumulated: number
  lastAnswers: boolean[] // Last 5 answers (true = correct, false = wrong)
  streak: number // Current consecutive correct answers
}

interface FreemiumState {
  // Question tracking (Feed)
  questionsUsed: number
  questionsRemaining: number

  // First Trophy Journey Mode (unlimited questions)
  isFirstTrophyJourneyActive: boolean

  // Paywall states (Feed)
  hasSeenSoftPaywall: boolean
  hasSeenHardPaywall: boolean
  softPaywallDismissedAt: number | null

  // Session stats for anonymous users
  sessionStats: FreemiumStats

  // Feature-specific usage tracking
  flashcardsViewed: number
  signsViewed: number
  simuladosUsed: number

  // Actions - Questions
  incrementQuestionsUsed: () => void
  markSoftPaywallSeen: () => void
  markHardPaywallSeen: () => void
  dismissSoftPaywall: () => void
  updateSessionStats: (correct: boolean, xp: number) => void
  resetFreemiumState: () => void
  setFirstTrophyJourneyActive: (active: boolean) => void

  // Actions - Feature tracking
  incrementFlashcardsViewed: () => void
  incrementSignsViewed: () => void
  incrementSimuladosUsed: () => void
  resetFeatureUsage: () => void

  // Computed helpers - Questions
  shouldShowSoftPaywall: () => boolean
  shouldShowHardPaywall: () => boolean
  isLimitReached: () => boolean
  getUrgencyLevel: () => 'normal' | 'warning' | 'critical'

  // Computed helpers - Features
  canAccessFlashcards: () => boolean
  canAccessSigns: () => boolean
  canAccessSimulados: () => boolean
  getFeatureRemaining: (feature: FreemiumFeature) => number
  isFeatureLimitReached: (feature: FreemiumFeature) => boolean
}

// =====================================
// Initial State
// =====================================

const initialState = {
  questionsUsed: 0,
  questionsRemaining: FALLBACK_QUESTIONS_LIMIT,
  isFirstTrophyJourneyActive: false,
  hasSeenSoftPaywall: false,
  hasSeenHardPaywall: false,
  softPaywallDismissedAt: null,
  sessionStats: {
    totalCorrect: 0,
    totalAnswered: 0,
    xpAccumulated: 0,
    lastAnswers: [],
    streak: 0,
  },
  flashcardsViewed: 0,
  signsViewed: 0,
  simuladosUsed: 0,
}

// =====================================
// Store Creation (SSR-safe)
// =====================================

export const useFreemiumStore = create<FreemiumState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // =====================================
      // Question Actions (Feed)
      // =====================================
      incrementQuestionsUsed: () => {
        set((state) => ({
          questionsUsed: state.questionsUsed + 1,
          questionsRemaining: Math.max(0, FALLBACK_QUESTIONS_LIMIT - state.questionsUsed - 1),
        }))
      },

      markSoftPaywallSeen: () => {
        set({ hasSeenSoftPaywall: true })
      },

      markHardPaywallSeen: () => {
        set({ hasSeenHardPaywall: true })
      },

      dismissSoftPaywall: () => {
        set({ softPaywallDismissedAt: Date.now() })
      },

      updateSessionStats: (correct: boolean, xp: number) => {
        set((state) => {
          const currentStats = state.sessionStats || {
            totalCorrect: 0,
            totalAnswered: 0,
            xpAccumulated: 0,
            lastAnswers: [],
            streak: 0,
          }

          const currentLastAnswers = Array.isArray(currentStats.lastAnswers)
            ? currentStats.lastAnswers
            : []
          const newLastAnswers = [...currentLastAnswers, correct].slice(-5)
          const newStreak = correct ? (currentStats.streak || 0) + 1 : 0

          return {
            sessionStats: {
              totalCorrect: (currentStats.totalCorrect || 0) + (correct ? 1 : 0),
              totalAnswered: (currentStats.totalAnswered || 0) + 1,
              xpAccumulated: (currentStats.xpAccumulated || 0) + xp,
              lastAnswers: newLastAnswers,
              streak: newStreak,
            },
          }
        })
      },

      resetFreemiumState: () => {
        set(initialState)
      },

      setFirstTrophyJourneyActive: (active: boolean) => {
        set({ isFirstTrophyJourneyActive: active })
      },

      // =====================================
      // Feature Usage Actions
      // =====================================
      incrementFlashcardsViewed: () => {
        set((state) => ({
          flashcardsViewed: state.flashcardsViewed + 1,
        }))
      },

      incrementSignsViewed: () => {
        set((state) => ({
          signsViewed: state.signsViewed + 1,
        }))
      },

      incrementSimuladosUsed: () => {
        set((state) => ({
          simuladosUsed: state.simuladosUsed + 1,
        }))
      },

      resetFeatureUsage: () => {
        set({
          flashcardsViewed: 0,
          signsViewed: 0,
          simuladosUsed: 0,
        })
      },

      // =====================================
      // Question Computed Helpers
      // =====================================
      shouldShowSoftPaywall: () => {
        const state = get()
        if (state.isFirstTrophyJourneyActive) return false

        const isAtThreshold = state.questionsUsed === FALLBACK_SOFT_PAYWALL_THRESHOLD
        const recentlyDismissed = state.softPaywallDismissedAt &&
          (Date.now() - state.softPaywallDismissedAt) < 60 * 60 * 1000 // 1 hour

        return isAtThreshold && !recentlyDismissed
      },

      shouldShowHardPaywall: () => {
        const state = get()
        if (state.isFirstTrophyJourneyActive) return false
        return state.questionsUsed >= FALLBACK_QUESTIONS_LIMIT
      },

      isLimitReached: () => {
        const state = get()
        if (state.isFirstTrophyJourneyActive) return false
        return state.questionsUsed >= FALLBACK_QUESTIONS_LIMIT
      },

      getUrgencyLevel: () => {
        const state = get()
        const remaining = state.questionsRemaining

        if (remaining <= 3) return 'critical'
        if (remaining <= 7) return 'warning'
        return 'normal'
      },

      // =====================================
      // Feature Computed Helpers
      // =====================================
      canAccessFlashcards: () => {
        const state = get()
        return state.flashcardsViewed < FREE_FLASHCARDS_LIMIT
      },

      canAccessSigns: () => {
        const state = get()
        return state.signsViewed < FREE_SIGNS_LIMIT
      },

      canAccessSimulados: () => {
        const state = get()
        return state.simuladosUsed < FREE_SIMULADOS_LIMIT
      },

      getFeatureRemaining: (feature: FreemiumFeature) => {
        const state = get()
        switch (feature) {
          case 'flashcards':
            return Math.max(0, FREE_FLASHCARDS_LIMIT - state.flashcardsViewed)
          case 'signs':
            return Math.max(0, FREE_SIGNS_LIMIT - state.signsViewed)
          case 'simulados':
            return Math.max(0, FREE_SIMULADOS_LIMIT - state.simuladosUsed)
        }
      },

      isFeatureLimitReached: (feature: FreemiumFeature) => {
        const state = get()
        switch (feature) {
          case 'flashcards':
            return state.flashcardsViewed >= FREE_FLASHCARDS_LIMIT
          case 'signs':
            return state.signsViewed >= FREE_SIGNS_LIMIT
          case 'simulados':
            return state.simuladosUsed >= FREE_SIMULADOS_LIMIT
        }
      },
    }),
    {
      name: 'cnh-pass-freemium',
      // SSR-safe storage - only use localStorage on client
      storage: createJSONStorage(() =>
        typeof window !== 'undefined' ? localStorage : ({
          getItem: () => null,
          setItem: () => {},
          removeItem: () => {},
        })
      ),
      // Skip automatic hydration to prevent SSR mismatches
      skipHydration: true,
      // Specify what to persist
      partialize: (state) => ({
        questionsUsed: state.questionsUsed,
        questionsRemaining: state.questionsRemaining,
        isFirstTrophyJourneyActive: state.isFirstTrophyJourneyActive,
        hasSeenSoftPaywall: state.hasSeenSoftPaywall,
        softPaywallDismissedAt: state.softPaywallDismissedAt,
        sessionStats: state.sessionStats,
        flashcardsViewed: state.flashcardsViewed,
        signsViewed: state.signsViewed,
        simuladosUsed: state.simuladosUsed,
      }),
      // Merge persisted state with initial state to handle schema changes
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<FreemiumState> | undefined
        const persistedStats = persisted?.sessionStats
        return {
          ...currentState,
          ...persisted,
          sessionStats: {
            totalCorrect: persistedStats?.totalCorrect ?? 0,
            totalAnswered: persistedStats?.totalAnswered ?? 0,
            xpAccumulated: persistedStats?.xpAccumulated ?? 0,
            streak: persistedStats?.streak ?? 0,
            lastAnswers: Array.isArray(persistedStats?.lastAnswers)
              ? persistedStats.lastAnswers
              : [],
          },
        }
      },
    }
  )
)

// =====================================
// Client-side hydration
// =====================================

// Rehydrate store on client-side
if (typeof window !== 'undefined') {
  useFreemiumStore.persist.rehydrate()
}

// =====================================
// Selectors (optimized for performance)
// =====================================

export const selectQuestionsRemaining = (state: FreemiumState): number =>
  state.questionsRemaining

export const selectSessionAccuracy = (state: FreemiumState): number => {
  const total = state.sessionStats.totalAnswered
  if (total === 0) return 0
  return Math.round((state.sessionStats.totalCorrect / total) * 100)
}

export const selectCurrentStreak = (state: FreemiumState): number =>
  state.sessionStats.streak

export const selectCanAccessFeature = (
  state: FreemiumState,
  feature: FreemiumFeature
): boolean => {
  switch (feature) {
    case 'flashcards':
      return state.canAccessFlashcards()
    case 'signs':
      return state.canAccessSigns()
    case 'simulados':
      return state.canAccessSimulados()
  }
}
